import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

/**
 * 第三方登录回调处理组件
 * 用于处理从第三方认证服务返回的授权码
 */
const ThirdPartyCallback = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithThirdParty } = useAuth();

  // 检查当前窗口是否是弹窗
  const isPopup = window.opener && window.opener !== window;

  // 从URL中提取provider信息
  const getProviderFromState = (): "google" | "apple" | null => {
    const state = new URLSearchParams(location.search).get('state');
    if (!state) return null;

    try {
      const stateObj = JSON.parse(decodeURIComponent(state));
      if (stateObj.provider === 'google') return 'google';
      if (stateObj.provider === 'apple') return 'apple';
    } catch (e) {
      console.error('Failed to parse state parameter:', e);
    }

    // 如果无法从state参数确定，尝试根据URL路径猜测
    const path = location.pathname.toLowerCase();
    if (path.includes('google')) return 'google';
    if (path.includes('apple')) return 'apple';

    return null;
  };

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // 检查是否有授权码和提供商
        let code: string | null = null;
        let provider: "google" | "apple" | null = null;
        let idToken: string | undefined = undefined;

        // 尝试从URL查询参数获取授权码
        code = new URLSearchParams(location.search).get('code');
        
        // 处理Apple表单提交方式的响应 (form_post response_mode)
        if (!code && location.search.length === 0 && document.forms.length > 0) {
          // 如果URL没有查询参数但有表单，这可能是Apple的表单提交方式
          const appleForm = document.forms[0];
          if (appleForm) {
            // 从表单元素获取code和id_token
            const codeInput = appleForm.elements.namedItem('code') as HTMLInputElement;
            const idTokenInput = appleForm.elements.namedItem('id_token') as HTMLInputElement;
            const stateInput = appleForm.elements.namedItem('state') as HTMLInputElement;
            
            if (codeInput) {
              code = codeInput.value;
              provider = 'apple';
            }
            
            if (idTokenInput) {
              idToken = idTokenInput.value;
            }
            
            if (!provider && stateInput && stateInput.value) {
              try {
                const stateObj = JSON.parse(decodeURIComponent(stateInput.value));
                if (stateObj.provider) {
                  provider = stateObj.provider as "google" | "apple";
                }
              } catch (e) {
                console.error('Failed to parse state from form:', e);
              }
            }
          }
        }

        // 如果仍然没有找到授权码，则报错
        if (!code) {
          throw new Error('No authorization code found in the URL or form');
        }

        // 如果没有确定提供商，尝试从URL确定
        if (!provider) {
          provider = getProviderFromState();
        }

        if (!provider) {
          throw new Error('Unable to determine the OAuth provider');
        }

        console.log(`Processing ${provider} OAuth callback with code:`, code);

        // 如果是弹窗环境，不需要处理完整的登录流程
        // 父窗口会通过监听URL变化来处理登录
        if (isPopup) {
          // 弹窗环境下，我们只设置页面状态，父窗口会处理登录
          setLoading(false);
          return;
        }

        // 构造适当的回调 URI - 必须与请求时完全匹配
        let redirectUri;
        if (provider === 'google') {
          // 获取环境变量中的重定向URI或使用默认值
          redirectUri = import.meta.env.VITE_GOOGLE_REDIRECT_URI || 
                        `${window.location.origin}/auth/google/callback`;
          
          // 检查URL中是否有device_id和device_name参数
          const deviceId = new URLSearchParams(location.search).get('device_id');
          const deviceName = new URLSearchParams(location.search).get('device_name');
          
          // 如果存在device参数，则需要包含这些参数作为redirectUri的一部分
          // 这是因为Google OAuth要求请求和回调的redirectUri必须完全匹配
          if (deviceId && deviceName) {
            redirectUri = `${redirectUri}?device_id=${encodeURIComponent(deviceId)}&device_name=${encodeURIComponent(deviceName)}`;
            console.log('Using redirectUri with device params:', redirectUri);
          }
        } else if (provider === 'apple') {
          redirectUri = import.meta.env.VITE_APPLE_REDIRECT_URI || 
                       `${window.location.origin}/auth/apple/callback`;
        }

        console.log(`Calling loginWithThirdParty for ${provider} with redirectUri:`, redirectUri);
        
        // 使用新的 loginWithThirdParty 方法
        try {
          await loginWithThirdParty(provider, code, idToken, redirectUri);
          console.log(`${provider} login successful`);
          
          // 为任何第三方登录设置资料完整性检查标记
          sessionStorage.setItem('checkProfileCompleteness', provider);
          console.log(`Set flag to check profile completeness for ${provider} login after redirect`);
        } catch (loginError) {
          console.error(`${provider} login error:`, loginError);
          throw new Error(loginError instanceof Error ? loginError.message : `${provider} login failed`);
        }

        // 登录成功，跳转到主页或指定的重定向页面
        const redirectTo = sessionStorage.getItem('authRedirectTo') || '/';
        sessionStorage.removeItem('authRedirectTo');
        
        toast.success(`${provider.charAt(0).toUpperCase() + provider.slice(1)} Login Successful`, {
          description: 'You have successfully logged in.',
        });
        
        navigate(redirectTo, { replace: true });
      } catch (err) {
        console.error('Error during third-party login callback:', err);
        setError(err instanceof Error ? err.message : 'Failed to complete authentication');
        
        toast.error('Authentication Failed', {
          description: err instanceof Error ? err.message : 'Failed to complete authentication',
        });
        
        // 出错时跳转到登录页
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 3000);
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [location, loginWithThirdParty, navigate, isPopup]);

  if (isPopup) {
    // 在弹窗中显示简化版本的UI
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        {loading ? (
          <>
            <Loader2 className="h-10 w-10 text-anzir animate-spin mb-4" />
            <h2 className="text-lg font-medium text-center">Authentication Complete</h2>
            <p className="text-gray-500 mt-2 text-center">You can close this window now</p>
          </>
        ) : error ? (
          <div className="bg-red-500/10 text-red-500 p-4 rounded-md max-w-md text-center">
            <h2 className="text-lg font-medium">Authentication Error</h2>
            <p className="mt-1">{error}</p>
            <p className="mt-3 text-sm">You can close this window and try again</p>
          </div>
        ) : (
          <div className="text-center">
            <h2 className="text-lg font-medium text-green-600">Authentication Successful</h2>
            <p className="text-gray-500 mt-2">You can close this window now</p>
            <button
              onClick={() => window.close()}
              className="mt-4 px-4 py-2 bg-anzir text-white rounded-md"
            >
              Close Window
            </button>
          </div>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-10 w-10 text-anzir animate-spin mb-4" />
        <h2 className="text-lg font-medium">Completing authentication...</h2>
        <p className="text-gray-500 mt-2">Please wait while we process your login</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="bg-red-500/10 text-red-500 p-4 rounded-md mb-4">
          <h2 className="text-lg font-medium">Authentication Error</h2>
          <p className="mt-1">{error}</p>
        </div>
        <button
          onClick={() => navigate('/login')}
          className="px-4 py-2 bg-anzir text-white rounded-md"
        >
          Return to Login
        </button>
      </div>
    );
  }

  // 通常不会到达这里，因为会在处理完成后跳转
  return null;
};

export default ThirdPartyCallback;
